import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactFlow, {
    Connection,
    NodeChange,
    Edge,
    EdgeLabelRenderer,
    EdgeProps,
    Handle,
    Node,
    NodeProps,
    NodeToolbar,
    Position,
    ReactFlowProvider,
    BaseEdge,
    getSmoothStepPath,
    useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ArrowDownToLine, ArrowUpToLine, Building2, LayoutGrid, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
    OrganizationChart,
    addCompany,
    addShareholder,
    addSubsidiary,
    balanceShares,
    canConnect,
    connectCompanies,
    emptyOrganizationChart,
    removeCompany,
    removeShareholding,
    renameCompany,
} from '@the-real-insight/tri-model';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { currentLocale } from '../components/numericFormat';
import {
    NODE_HEIGHT,
    NODE_WIDTH,
    freeSpotNear,
    isPlacedByHand,
    positionsOf,
    withPositions,
} from './organizationChartLayout';
import { useQueuedWrite } from './useQueuedWrite';

/**
 * "Organisationshierarchie": the companies behind a project as a graph of shareholdings.
 *
 * A company is a box, a shareholding an arrow from the holder down to what it holds, labelled with
 * the share. Hovering a box opens a small menu beside it — a new holder above, a new holding below,
 * rename, remove. The share is edited on the arrow itself. The holders of a company always hold
 * 100 percent together: changing one share moves the others (see `balanceShares` in the model), so
 * there is no state in which the chart is half-edited and wrong.
 *
 * Every change is written at once and whole — there is no Save button, like everywhere else in the
 * drill-down. The chart lays itself out until a box is dragged; from then on every box keeps the
 * place it was given, and "Automatisch anordnen" hands the layout back to the computation.
 */

interface Properties {
    value: OrganizationChart | null | undefined;
    /** Writes the whole chart. Rejects when the write failed; the editor then shows what is stored. */
    onChange: (next: OrganizationChart) => Promise<void>;
    disabled?: boolean;
}

interface EditorContext {
    chart: OrganizationChart;
    editable: boolean;
    /** The company whose name is being typed. A new company starts out in it. */
    renaming: string | null;
    setRenaming: (id: string | null) => void;
    apply: (next: OrganizationChart, focus?: string) => void;
    /** A new company above (a holder) or below (a holding) the given one, named straight away. */
    addNear: (anchorId: string, direction: 'above' | 'below') => void;
}

const Context = createContext<EditorContext | null>(null);
const useEditor = () => useContext(Context)!;

const newId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const formatShare = (share: number): string =>
    `${new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 2 }).format(share)} %`;

/** How long the menu stays after the pointer leaves the box — enough to reach the menu. */
const CLOSE_DELAY_MS = 180;

function CompanyNode({ id, data, dragging }: NodeProps<{ name: string }>) {
    const { t } = useTranslation();
    const { chart, editable, renaming, setRenaming, apply, addNear } = useEditor();
    const [menuOpen, setMenuOpen] = useState(false);
    const [draft, setDraft] = useState(data.name);
    const closeTimer = useRef<number>();
    /** Escape leaves the field too; this keeps that blur from writing what was typed. */
    const cancelled = useRef(false);
    const editing = renaming === id;

    useEffect(() => {
        if (editing) setDraft(data.name);
    }, [editing, data.name]);

    useEffect(() => () => window.clearTimeout(closeTimer.current), []);

    const open = () => {
        window.clearTimeout(closeTimer.current);
        if (editable) setMenuOpen(true);
    };
    const close = () => {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = window.setTimeout(() => setMenuOpen(false), CLOSE_DELAY_MS);
    };

    const commitName = () => {
        setRenaming(null);
        if (cancelled.current) {
            cancelled.current = false;
            return;
        }
        const name = draft.trim();
        if (name && name !== data.name) apply(renameCompany(chart, id, name));
    };

    const add = (direction: 'above' | 'below') => {
        setMenuOpen(false);
        addNear(id, direction);
    };

    const menu: { label: string; icon: JSX.Element; onClick: () => void; danger?: boolean }[] = [
        { label: t('portfolioDashboard.organizationChart.addShareholder'), icon: <ArrowUpToLine size={14} />, onClick: () => add('above') },
        { label: t('portfolioDashboard.organizationChart.addSubsidiary'), icon: <ArrowDownToLine size={14} />, onClick: () => add('below') },
        {
            label: t('portfolioDashboard.organizationChart.rename'),
            icon: <Pencil size={14} />,
            onClick: () => {
                setMenuOpen(false);
                setRenaming(id);
            },
        },
        {
            label: t('portfolioDashboard.organizationChart.remove'),
            icon: <Trash2 size={14} />,
            onClick: () => {
                setMenuOpen(false);
                apply(removeCompany(chart, id));
            },
            danger: true,
        },
    ];

    return (
        <div
            onMouseEnter={open}
            onMouseLeave={close}
            onFocus={open}
            onBlur={close}
            onDoubleClick={() => editable && setRenaming(id)}
            className={cn(
                'flex items-center gap-2 rounded-lg border bg-card px-3 text-sm shadow-sm',
                editable && 'cursor-grab active:cursor-grabbing',
            )}
            style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}
        >
            <Handle type="target" position={Position.Top} isConnectable={editable} className="!h-2 !w-2 !bg-muted-foreground" />
            <Building2 size={14} className="shrink-0 text-muted-foreground" aria-hidden />
            {editing ? (
                <input
                    autoFocus
                    value={draft}
                    aria-label={t('portfolioDashboard.organizationChart.name')}
                    className="nodrag min-w-0 flex-1 rounded border bg-background px-1 py-0.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onChange={(event) => setDraft(event.target.value)}
                    onFocus={(event) => event.target.select()}
                    onBlur={commitName}
                    // Enter leaves the field, and leaving it is what commits — one path, one write.
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') event.currentTarget.blur();
                        if (event.key === 'Escape') {
                            cancelled.current = true;
                            event.currentTarget.blur();
                        }
                    }}
                />
            ) : (
                <span className="min-w-0 flex-1 truncate font-medium" title={data.name}>
                    {data.name || t('portfolioDashboard.organizationChart.unnamed')}
                </span>
            )}
            {editable ? (
                // Focusable, so the menu is reachable without a pointer: tabbing onto the box opens it.
                <button
                    type="button"
                    className="nodrag shrink-0 rounded p-0.5 text-muted-foreground opacity-0 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={t('portfolioDashboard.organizationChart.menu', { name: data.name })}
                    onClick={open}
                >
                    <Plus size={12} />
                </button>
            ) : (
                <></>
            )}
            <Handle type="source" position={Position.Bottom} isConnectable={editable} className="!h-2 !w-2 !bg-muted-foreground" />

            <NodeToolbar isVisible={menuOpen && !editing && !dragging} position={Position.Right} offset={6}>
                <div
                    role="menu"
                    aria-label={t('portfolioDashboard.organizationChart.menu', { name: data.name })}
                    onMouseEnter={open}
                    onMouseLeave={close}
                    onFocus={open}
                    onBlur={close}
                    className="flex flex-col rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
                >
                    {menu.map((entry) => (
                        <button
                            key={entry.label}
                            type="button"
                            role="menuitem"
                            onClick={entry.onClick}
                            className={cn(
                                'flex items-center gap-2 whitespace-nowrap rounded-sm px-2 py-1.5 text-left text-xs hover:bg-accent focus-visible:bg-accent focus-visible:outline-none',
                                entry.danger && 'text-destructive',
                            )}
                        >
                            {entry.icon}
                            {entry.label}
                        </button>
                    ))}
                </div>
            </NodeToolbar>
        </div>
    );
}

function ShareholdingEdge({ id, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, markerEnd, data }: EdgeProps<{ share: number }>) {
    const { t } = useTranslation();
    const { chart, editable, apply } = useEditor();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const cancelled = useRef(false);
    const [path, labelX, labelY] = getSmoothStepPath({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition });
    const share = data?.share ?? 0;
    // A company with one holder is held entirely; there is nothing to edit on that arrow.
    const soleHolder = chart.shareholdings.filter((entry) => entry.childId === target).length === 1;

    const commit = () => {
        setEditing(false);
        if (cancelled.current) {
            cancelled.current = false;
            return;
        }
        const parsed = Number(draft.replace(',', '.'));
        if (Number.isFinite(parsed) && parsed !== share) apply(balanceShares(chart, source, target, parsed));
    };

    const holderName = chart.companies.find((company) => company.id === source)?.name ?? '';
    const heldName = chart.companies.find((company) => company.id === target)?.name ?? '';

    return (
        <>
            <BaseEdge id={id} path={path} markerEnd={markerEnd} />
            <EdgeLabelRenderer>
                <div
                    className="nodrag nopan group absolute flex items-center gap-0.5"
                    style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`, pointerEvents: 'all' }}
                >
                    {editing ? (
                        <input
                            autoFocus
                            inputMode="decimal"
                            value={draft}
                            aria-label={t('portfolioDashboard.organizationChart.share', { holder: holderName, held: heldName })}
                            className="w-16 rounded border bg-background px-1 py-0.5 text-right text-xs tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onChange={(event) => setDraft(event.target.value)}
                            onFocus={(event) => event.target.select()}
                            onBlur={commit}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') event.currentTarget.blur();
                                if (event.key === 'Escape') {
                                    cancelled.current = true;
                                    event.currentTarget.blur();
                                }
                            }}
                        />
                    ) : editable && !soleHolder ? (
                        // Editable shares look like it — a pen and a border that answers the pointer.
                        // A share that cannot move must not look the same, or the reader clicks on
                        // every 100 % and nothing happens.
                        <button
                            type="button"
                            title={t('portfolioDashboard.organizationChart.editShare')}
                            aria-label={t('portfolioDashboard.organizationChart.share', { holder: holderName, held: heldName })}
                            onClick={() => {
                                setDraft(String(share).replace('.', currentLocale().startsWith('de') ? ',' : '.'));
                                setEditing(true);
                            }}
                            className="flex cursor-pointer items-center gap-1 rounded-full border border-primary/40 bg-background px-2 py-0.5 text-xs font-semibold tabular-nums shadow-sm hover:border-primary hover:bg-accent"
                        >
                            {formatShare(share)}
                            <Pencil size={10} className="text-muted-foreground" aria-hidden />
                        </button>
                    ) : (
                        <span
                            title={soleHolder ? t('portfolioDashboard.organizationChart.soleHolder') : undefined}
                            className="rounded-full bg-background/80 px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground"
                        >
                            {formatShare(share)}
                        </span>
                    )}
                    {editable && !editing ? (
                        <button
                            type="button"
                            aria-label={t('portfolioDashboard.organizationChart.removeShareholding', { holder: holderName, held: heldName })}
                            onClick={() => apply(removeShareholding(chart, source, target))}
                            className="rounded-full border bg-background p-0.5 text-muted-foreground opacity-0 shadow-sm hover:text-destructive focus:opacity-100 group-hover:opacity-100"
                        >
                            <X size={10} />
                        </button>
                    ) : (
                        <></>
                    )}
                </div>
            </EdgeLabelRenderer>
        </>
    );
}

const nodeTypes = { company: CompanyNode };
const edgeTypes = { shareholding: ShareholdingEdge };

/**
 * Frames the whole chart whenever a company comes or goes, and after "Automatisch anordnen". Not
 * after a drag: the viewport moving away the moment a box was put down would undo the placing.
 */
function FitOnChange({ count, layout }: { count: number; layout: number }) {
    const { fitView } = useReactFlow();

    useEffect(() => {
        const frame = window.requestAnimationFrame(() => fitView({ padding: 0.25, duration: 200, maxZoom: 1 }));
        return () => window.cancelAnimationFrame(frame);
    }, [count, layout, fitView]);

    return null;
}

function Editor({ value, onChange, disabled }: Properties) {
    const { t } = useTranslation();
    const stored = useMemo(() => value ?? emptyOrganizationChart(), [value]);
    const describeError = useCallback((error: any) => error?.message ?? t('global.generalError'), [t]);
    const { value: chart, apply: write, error } = useQueuedWrite(stored, onChange, describeError);
    const [renaming, setRenaming] = useState<string | null>(null);
    const editable = !disabled;

    const apply = useCallback(
        (next: OrganizationChart, focus?: string) => {
            if (focus) setRenaming(focus);
            write(next);
        },
        [write],
    );

    const newCompanyName = t('portfolioDashboard.organizationChart.newCompany');
    const placedByHand = isPlacedByHand(chart);
    const positions = useMemo(() => positionsOf(chart), [chart]);
    /**
     * Where boxes stand while they are being dragged. Only the end of a drag is written — one write
     * per move, not one per pixel.
     */
    const [dragged, setDragged] = useState<Record<string, { x: number; y: number }>>({});

    /**
     * Once anything was placed by hand, a new company gets a place of its own next to the one it
     * was added from, and every other box stays where it was put. Before that, the automatic layout
     * places it like everything else.
     */
    const placed = useCallback(
        (next: OrganizationChart, id: string, anchor: { x: number; y: number }, direction: 'above' | 'below' | 'beside') =>
            placedByHand ? withPositions(next, new Map([[id, freeSpotNear(positions.values(), anchor, direction)]])) : next,
        [placedByHand, positions],
    );

    const addNear = useCallback(
        (anchorId: string, direction: 'above' | 'below') => {
            const company = { id: newId(), name: newCompanyName };
            const next =
                direction === 'above' ? addShareholder(chart, anchorId, company) : addSubsidiary(chart, anchorId, company);
            apply(placed(next, company.id, positions.get(anchorId) ?? { x: 0, y: 0 }, direction), company.id);
        },
        [apply, chart, newCompanyName, placed, positions],
    );

    const onNodesChange = useCallback((changes: NodeChange[]) => {
        const moves = changes.filter(
            (change): change is Extract<NodeChange, { type: 'position' }> =>
                change.type === 'position' && !!change.position,
        );
        if (moves.length > 0) {
            setDragged((current) => ({
                ...current,
                ...Object.fromEntries(moves.map((change) => [change.id, change.position!])),
            }));
        }
    }, []);

    const onNodeDragStop = (_event: unknown, node: Node) => {
        // The first move pins every box where it stands, so the others do not jump when the
        // automatic layout stops applying.
        const all = new Map(positions);
        Object.entries(dragged).forEach(([id, position]) => all.set(id, position));
        all.set(node.id, node.position);
        setDragged({});
        apply(withPositions(chart, all));
    };

    /** Counts the returns to the automatic layout — each one reframes the chart. */
    const [arranged, setArranged] = useState(0);

    const arrangeAutomatically = () => {
        setArranged((count) => count + 1);
        apply({ ...chart, companies: chart.companies.map(({ position: _position, ...company }) => company) });
    };

    const { nodes, edges } = useMemo(() => {
        const laidOut: Node[] = chart.companies.map((company) => ({
            id: company.id,
            type: 'company',
            position: dragged[company.id] ?? positions.get(company.id) ?? { x: 0, y: 0 },
            data: { name: company.name },
            draggable: editable,
            selectable: false,
            // React Flow takes pointer events away from a node that can be neither dragged nor
            // selected. The hover menu and the double-click need them on the whole box, not only
            // on the handles — also where the chart is read-only.
            style: { pointerEvents: 'all' },
        }));
        const arrows: Edge[] = chart.shareholdings.map((entry) => ({
            id: `${entry.parentId}->${entry.childId}`,
            source: entry.parentId,
            target: entry.childId,
            type: 'shareholding',
            data: { share: entry.share },
            markerEnd: { type: 'arrowclosed' as any },
        }));
        return { nodes: laidOut, edges: arrows };
    }, [chart, dragged, positions, editable]);

    const context = useMemo<EditorContext>(
        () => ({ chart, editable, renaming, setRenaming, apply, addNear }),
        [chart, editable, renaming, apply, addNear],
    );

    /** A company without holder — beside the top row, so it does not land on anything. */
    const addTop = () => {
        const company = { id: newId(), name: newCompanyName };
        const all = Array.from(positions.values());
        const top = all.length ? Math.min(...all.map((point) => point.y)) : 0;
        const right = all.filter((point) => point.y === top).reduce((best, point) => (point.x > best.x ? point : best), { x: 0, y: top });
        apply(placed(addCompany(chart, company), company.id, right, 'beside'), company.id);
    };

    const onConnect = (connection: Connection) => {
        if (connection.source && connection.target) apply(connectCompanies(chart, connection.source, connection.target));
    };

    return (
        <Context.Provider value={context}>
            {chart.companies.length === 0 ? (
                <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                    {t('portfolioDashboard.organizationChart.empty')}
                    {editable ? (
                        <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={addTop}>
                            <Plus size={14} />
                            {t('portfolioDashboard.organizationChart.addCompany')}
                        </Button>
                    ) : (
                        <></>
                    )}
                </div>
            ) : (
                <>
                    <div className="h-[380px] overflow-hidden rounded-xl border bg-muted/30">
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            nodesDraggable={editable}
                            nodesConnectable={editable}
                            elementsSelectable={false}
                            onNodesChange={onNodesChange}
                            onNodeDragStop={onNodeDragStop}
                            onConnect={onConnect}
                            isValidConnection={(connection) =>
                                !!connection.source &&
                                !!connection.target &&
                                canConnect(chart, connection.source, connection.target)
                            }
                            // A wheel over the chart scrolls the drill-down; zooming is by pinch or
                            // the controls of the browser, as with the map in the chat.
                            zoomOnScroll={false}
                            panOnScroll={false}
                            preventScrolling={false}
                            minZoom={0.3}
                            maxZoom={1.5}
                            proOptions={{ hideAttribution: true }}
                        >
                            <FitOnChange count={chart.companies.length} layout={arranged} />
                        </ReactFlow>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground">
                            {editable ? t('portfolioDashboard.organizationChart.hint') : ''}
                        </span>
                        {editable ? (
                            <div className="flex gap-2">
                                {placedByHand ? (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="gap-1.5"
                                        onClick={arrangeAutomatically}
                                    >
                                        <LayoutGrid size={14} />
                                        {t('portfolioDashboard.organizationChart.arrange')}
                                    </Button>
                                ) : (
                                    <></>
                                )}
                                <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={addTop}>
                                    <Plus size={14} />
                                    {t('portfolioDashboard.organizationChart.addCompany')}
                                </Button>
                            </div>
                        ) : (
                            <></>
                        )}
                    </div>
                </>
            )}
            {error ? (
                <Alert variant="destructive" className="mt-3">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ) : (
                <></>
            )}
        </Context.Provider>
    );
}

export default function OrganizationChartEditor(properties: Properties) {
    return (
        <ReactFlowProvider>
            <Editor {...properties} />
        </ReactFlowProvider>
    );
}
